import React, { useState, useEffect } from "react";
import { DataToolbar } from "../components/DataToolbar";
import { Modal } from "../components/Modal";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { useToast } from "../components/Toast";
import { generateAutoNo } from "../services/counterService";
import { db, serverTimestamp } from "../../firebaseClient";
import { useAuth } from "../context/AuthContext";
import { canViewData } from "../utils/dataAccess";
import { isSuperUser } from "../config/roles";
import { AIPlaceholderButton } from "../components/ai/AIPlaceholderButton";
import { formatDate } from "../utils/dateUtils";
import { listRefsByEntity, createRef, deleteRef } from "../services/refService";
import { setupDemoLinkSet01 } from "../services/demoLinkageService";
import { SystemLinkagePanel } from "../components/shared/SystemLinkagePanel";
import type { RefRecord } from "../types/legalOps";
import { LitigationDocsPanel } from "../components/legal/LitigationDocsPanel";
import { useAiContext } from "../ai/AiContext";

export function LegalMattersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const { showToast } = useToast();
  const { setAiOpen, setDraft } = useAiContext();

  const isSuper = isSuperUser(user?.email);

  useEffect(() => {
    const unsub = db.collection("legal_matters").orderBy("createdAt", "desc").onSnapshot(snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const visible = all.filter(item => canViewData(user as any, item));
      setItems(visible);
      setLoading(false);
    });
    return () => unsub();
  }, [user]);

  const handleCreateDemo = async () => {
    try {
        await setupDemoLinkSet01(user?.email || "");
        showToast("示範資料建立完成 (DEMO_LINKSET_01)");
    } catch (e) { showToast("建立示範資料失敗", "error"); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("確定刪除此案件？")) return;
    try {
      await db.collection("legal_matters").doc(id).delete();
      showToast("已刪除");
    } catch (e) {
      showToast("刪除失敗", "error");
    }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* PkgB: Rational Max-Width Container */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', width: '100%', padding: '0 20px' }}>
        <DataToolbar 
          canEdit={true} 
          onCreate={() => { setEditingItem(null); setIsModalOpen(true); }} 
          onExport={() => {}} 
          title="案件及爭議管理"
          extraButtons={isSuper && (
              <button className="smallbtn" style={{ background: '#312e81', color: '#a5b4fc', border: '1px solid #4338ca' }} onClick={handleCreateDemo}>
                  ⚡ 建立示範串聯資料
              </button>
          )}
        />
        
        {/* PkgB: Card-style Grid UI */}
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', 
          gap: 20, 
          paddingBottom: 40 
        }}>
          {loading && <div style={{gridColumn:'1/-1', textAlign:'center', padding:40}}>載入中...</div>}
          
          {!loading && items.map(item => (
            <div 
              key={item.id} 
              className="tile" 
              style={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: 20,
                borderRadius: 12,
                background: '#1e1e1e',
                border: '1px solid #333',
                borderLeft: `5px solid ${item.riskLevel === 'HIGH' ? 'var(--danger)' : item.riskLevel === 'MEDIUM' ? '#fbbf24' : '#10b981'}` 
              }}
            >
              <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#fbbf24' }}>{item.matterNo}</span>
                    <span className="badge" style={{ fontSize: 10 }}>{item.caseCategory || "案件"}</span>
                  </div>
                  
                  <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 12, color: '#fff', lineHeight: 1.4 }}>
                    {item.title}
                  </div>
                  
                  <div style={{ fontSize: 13, color: '#aaa', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div><span style={{color: '#666'}}>對造：</span>{item.counterparty || "無"}</div>
                    <div><span style={{color: '#666'}}>主責：</span>{item.department}</div>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                      <span><span style={{color: '#666'}}>狀態：</span>{item.status}</span>
                      {item.linkKey && <span style={{ fontSize: 10, color: '#6366f1' }}>🔗 {item.linkKey}</span>}
                    </div>
                  </div>
              </div>
              
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #2a2a2a', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button className="smallbtn" style={{ padding: '4px 12px' }} onClick={() => { setEditingItem(item); setIsModalOpen(true); }}>詳情</button>
                  {isSuper && (
                    <button className="smallbtn" style={{ color: 'var(--danger)', borderColor: 'var(--danger)', padding: '4px 12px' }} onClick={() => handleDelete(item.id)}>刪除</button>
                  )}
              </div>
            </div>
          ))}
          
          {!loading && items.length === 0 && (
            <div style={{gridColumn:'1/-1', textAlign:'center', padding:60, color:'#666', border:'1px dashed #333', borderRadius:12}}>
              目前無案件資料
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <MatterForm 
          initialData={editingItem} 
          onClose={() => setIsModalOpen(false)} 
          onSuccess={() => setIsModalOpen(false)} 
        />
      )}
    </div>
  );
}



function deepOmitUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    // @ts-expect-error - generic array
    return value
      .map((v) => deepOmitUndefined(v))
      .filter((v) => v !== undefined) as any;
  }

  if (value && typeof value === "object") {
    const proto = Object.getPrototypeOf(value);
    // Keep special objects (e.g. firebase Timestamp/FieldValue) intact.
    if (proto !== Object.prototype && proto !== null) return value;

    const out: any = {};
    for (const [k, v] of Object.entries(value as any)) {
      if (v === undefined) continue;
      const cleaned = deepOmitUndefined(v);
      if (cleaned === undefined) continue;
      out[k] = cleaned;
    }
    return out;
  }

  return value;
}

function MatterForm({ initialData, onClose, onSuccess }: any) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { setAiOpen, setDraft } = useAiContext();
  const [activeTab, setActiveTab] = useState("basic");
  const [form, setForm] = useState(initialData || { 
    type: "案件", 
    caseCategory: "民事訴訟",
    status: "進行中", 
    riskLevel: "MEDIUM",
    department: user?.email ? user.email.split('@')[0].toUpperCase() : "LG",
    counterparty: "",
    courtOrAuthority: "",
    docketNo: "",
    parties: "",
    claimAmount: "",
    assignedOwner: "",
    summary: "",
    milestones: [],
    attachments: [],
    relatedNos: {}
  });
  const [refs, setRefs] = useState<RefRecord[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!initialData && !form.matterNo) {
        generateAutoNo("CASE", formatDate(new Date()).replace(/-/g, ""), 3).then(no => setForm((p:any)=>({...p, matterNo: no})));
    }
    if (initialData?.id) {
        listRefsByEntity(initialData.id).then(setRefs);
    }
  }, [initialData]);

  const handleSave = async () => {
    if (!form.title || !form.caseCategory) return alert("請填寫案名與分類");
    setSaving(true);
    try {
        const payload = { 
          ...form, 
          ownerUid: user?.id,
          updatedAt: serverTimestamp() 
        };
        const cleanedPayload = deepOmitUndefined(payload);
        if (initialData?.id) {
          await db.collection("legal_matters").doc(initialData.id).update(cleanedPayload);
        } else {
          await db.collection("legal_matters").add({ ...cleanedPayload, createdAt: serverTimestamp() });
        }
        showToast("存檔成功");
        onSuccess();
    } catch (e) {
        showToast(`存檔失敗: ${e?.message || e}`, "error");
    } finally {
        setSaving(false);
    }
  };

  const TabBtn = ({ id, label }: any) => (
    <button 
      className={`navbtn ${activeTab === id ? 'active' : ''}`} 
      style={{ width: 'auto', borderRadius: 0, borderBottom: activeTab === id ? '2px solid #fff' : 'none' }}
      onClick={() => setActiveTab(id)}
    >
      {label}
    </button>
  );

  return (
    <Modal title={initialData ? "編輯案件" : "新增案件"} onClose={onClose} width={950}>
      <div style={{ display: 'flex', borderBottom: '1px solid #333', marginBottom: 20, overflowX: 'auto' }}>
        <TabBtn id="basic" label="基本資料" />
        <TabBtn id="procedure" label="程序/案號" />
        <TabBtn id="parties" label="當事人/對造" />
        <TabBtn id="summary" label="摘要/里程碑" />
        {/* PkgB: New Tab Positioned per instruction */}
        <TabBtn id="litigationDocs" label="訴訟文件管理" />
        <TabBtn id="linkage" label="關聯Refs" />
      </div>

      <div style={{ minHeight: 450 }}>
        {activeTab === 'basic' && (
          <div className="grid">
            <div className="field"><label className="label">案號</label><input className="input" value={form.matterNo} readOnly style={{background:'#111', color:'#fbbf24'}} /></div>
            <div className="field"><label className="label">案件分類 *</label>
              <select className="input" value={form.caseCategory} onChange={e=>setForm({...form, caseCategory:e.target.value})}>
                <option>民事訴訟</option><option>民事非訟</option><option>刑事訴訟</option><option>行政訴訟</option><option>智財維權</option><option>其他</option>
              </select>
            </div>
            <div className="field" style={{gridColumn:'1/-1'}}><label className="label">案名 *</label><input className="input" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} /></div>
            <div className="field"><label className="label">案件狀態</label>
              <select className="input" value={form.status} onChange={e=>setForm({...form, status:e.target.value})}>
                <option>進行中</option><option>已結案</option><option>暫停</option><option>評估中</option>
              </select>
            </div>
            <div className="field"><label className="label">主責部門</label><input className="input" value={form.department} onChange={e=>setForm({...form, department:e.target.value})} /></div>
            <div className="field"><label className="label">承辦人/外部律師</label><input className="input" value={form.assignedOwner} onChange={e=>setForm({...form, assignedOwner:e.target.value})} /></div>
            <div className="field"><label className="label">風險等級</label>
              <select className="input" value={form.riskLevel} onChange={e=>setForm({...form, riskLevel:e.target.value})}>
                <option value="LOW">低風險</option><option value="MEDIUM">中風險</option><option value="HIGH">高風險</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 'procedure' && (
          <div className="grid">
            <div className="field"><label className="label">受理法院/機關</label><input className="input" value={form.courtOrAuthority} onChange={e=>setForm({...form, courtOrAuthority:e.target.value})} /></div>
            <div className="field"><label className="label">法院字號/案號</label><input className="input" value={form.docketNo} onChange={e=>setForm({...form, docketNo:e.target.value})} /></div>
            <div className="field"><label className="label">標題金額/求償金額</label><input className="input" value={form.claimAmount} onChange={e=>setForm({...form, claimAmount:e.target.value})} /></div>
            <div className="field"><label className="label">下次期限/開庭日</label><input type="date" className="input" value={form.nextActionAt} onChange={e=>setForm({...form, nextActionAt:e.target.value})} /></div>
          </div>
        )}

        {activeTab === 'parties' && (
          <div className="field">
            <label className="label">當事人/對造資訊</label>
            <textarea className="input" rows={6} value={form.parties} onChange={e=>setForm({...form, parties:e.target.value})} placeholder="例如：原告-某某公司公司；被告-XX實業有限公司..." />
            <div className="field" style={{marginTop:16}}><label className="label">對造名稱簡稱</label><input className="input" value={form.counterparty} onChange={e=>setForm({...form, counterparty:e.target.value})} /></div>
          </div>
        )}

        {activeTab === 'summary' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="field">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}><label className="label">案情摘要</label><AIPlaceholderButton
                    type="summary"
                    onClick={() => {
                      setDraft({
                        type: "case",
                        data: {
                          title: form.title || "(未命名案件)",
                          caseCategory: form.caseCategory,
                          stage: form.stage,
                          summary: form.summary,
                          parties: form.parties,
                          caseNumber: form.caseNumber,
                          court: form.court,
                          judge: form.judge,
                          nextActionAt: form.nextActionAt,
                          attachments: (form.litigationDocs || []).map((a: any) => ({
                            name: a.name,
                            url: a.url,
                            path: a.path,
                            contentType: a.contentType,
                            size: a.size,
                          })),
                        },
                        onApplyResult: (target: any, content: string) => {
                          if (target === "summary") {
                            setForm((prev) => ({ ...prev, summary: content }));
                          } else {
                            showToast("此處目前僅支援回填『摘要』，其他內容請手動複製貼上。", "info");
                          }
                        },
                      });
                      setAiOpen(true);
                    }}
                  /></div>
              <textarea className="input" rows={6} value={form.summary} onChange={e=>setForm({...form, summary:e.target.value})} />
            </div>
            <div className="section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <label className="label" style={{ margin: 0 }}>程序節點 (Milestones)</label>
                <button className="smallbtn" onClick={() => setForm({ ...form, milestones: [...(form.milestones || []), { label: "", date: "" }] })}>+新增</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {form.milestones?.map((m: any, i: number) => (
                  <div key={i} style={{ display: 'flex', gap: 8 }}>
                    <input type="date" className="input" style={{ width: 150 }} value={m.date} onChange={e => { const n = [...form.milestones]; n[i].date = e.target.value; setForm({ ...form, milestones: n }); }} />
                    <input className="input" style={{ flex: 1 }} value={m.label} onChange={e => { const n = [...form.milestones]; n[i].label = e.target.value; setForm({ ...form, milestones: n }); }} placeholder="例如：起訴、開庭、判決" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'litigationDocs' && (
          initialData?.id ? (
            <LitigationDocsPanel matterId={initialData.id} canEdit={true} />
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>請先儲存案件基本資料後，再開始管理訴訟文件。</div>
          )
        )}

        {activeTab === 'linkage' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <SystemLinkagePanel relatedNos={form.relatedNos} linkKey={form.linkKey} onChange={({ relatedNos, linkKey }) => setForm({ ...form, relatedNos, linkKey })} />
            <div className="section">
               <h4 className="label">Refs 關聯與附件</h4>
               <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
                  {refs.map(r => (
                    <div key={r.id} style={{ background:'rgba(255,255,255,0.05)', padding:8, borderRadius:6, fontSize:12 }}>
                      <b>{r.title}</b><br/><span style={{color:'#60a5fa'}}>{r.sourceUrl}</span>
                    </div>
                  ))}
               </div>
               <FileUploadKit label="案件附件" existingFiles={form.attachments} onFilesSelected={()=>{}} />
            </div>
          </div>
        )}
      </div>

      <div className="row" style={{ marginTop: 24, justifyContent: 'flex-end' }}>
        <button className="btn" style={{ width: 'auto' }} onClick={handleSave} disabled={saving}>儲存案件</button>
      </div>
    </Modal>
  );
}